import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useSEO } from "../lib/seo";
import { fadeInUp, HEADSHOT_URL } from "../lib/brand";
import { SERVICES } from "../data/services";
import { FRAMEWORK } from "../data/framework";
import { INSIGHTS } from "../data/insights";
import { Eyebrow, GoldRule, SectionHeading, PrimaryLink, SecondaryLink, TextLink } from "../components/site/Primitives";
import { FrameworkMountain, FrameworkSection } from "../components/site/FrameworkGraphic";
import { DimensionsGraphic } from "../components/site/DimensionsGraphic";
import { CTASection } from "../components/site/Sections";
import { RidgeLines } from "../components/site/Marks";

const stagger = (i) => ({ ...fadeInUp, transition: { ...fadeInUp.transition, delay: 0.08 * i } });
const reveal = (delay) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay } });

const Hero = () => (
  <section id="hero" data-testid="hero-section" className="relative min-h-screen flex items-center pt-32 pb-16 bg-summit-bg overflow-hidden">
    <RidgeLines className="absolute inset-x-0 bottom-0 h-72 w-full opacity-60 pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
      <div className="lg:col-span-7">
        <motion.div {...reveal(0)}>
          <Eyebrow className="mb-6">Where AI Strategy Meets Organizational Transformation</Eyebrow>
        </motion.div>
        <motion.h1 {...reveal(0.1)} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tight text-summit-charcoal">
          Turning AI Potential Into <span className="italic text-summit-gold-dark">Organizational Performance</span>
        </motion.h1>
        <motion.div {...reveal(0.3)}><GoldRule className="my-8" /></motion.div>
        <motion.p {...reveal(0.3)} className="font-body text-base md:text-lg text-summit-charcoal leading-relaxed max-w-xl">
          Summit helps organizations successfully adopt AI and lead complex organizational transformation—building the leadership and organizational capability that turns strategy into results.
        </motion.p>
        <motion.div {...reveal(0.45)} className="mt-10 flex flex-col sm:flex-row gap-4">
          <PrimaryLink to="/contact" testId="hero-primary-cta">Schedule a Strategy Conversation</PrimaryLink>
          <SecondaryLink to="/services/ai-strategy-adoption" testId="hero-secondary-cta">Explore Our Approach</SecondaryLink>
        </motion.div>
        <motion.ul {...reveal(0.6)} data-testid="hero-capabilities" className="mt-12 flex flex-wrap gap-x-6 gap-y-2 font-body text-xs uppercase tracking-[0.18em] text-summit-text-muted">
          {SERVICES.map((s) => (
            <li key={s.slug} className={s.lead ? "text-summit-gold-dark font-semibold" : ""}>{s.shortTitle}</li>
          ))}
        </motion.ul>
      </div>
      <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9, delay: 0.3 }} className="lg:col-span-5 relative">
        <FrameworkMountain showLabels={false} idPrefix="hero-framework-node" className="drop-shadow-[0_30px_50px_rgba(169,130,92,0.18)]" />
        <Link
          to="/services/organizational-transformation"
          data-testid="hero-framework-card"
          className="group mt-2 lg:absolute lg:-bottom-6 lg:left-4 flex items-center justify-between gap-6 bg-white/90 backdrop-blur border border-summit-border px-5 py-4 max-w-sm hover:border-summit-gold transition-colors"
        >
          <span>
            <span className="block font-body text-[10px] uppercase tracking-[0.2em] text-summit-gold-dark font-semibold">Proprietary Methodology</span>
            <span className="block font-heading text-lg text-summit-charcoal leading-tight mt-1">{FRAMEWORK.name}</span>
          </span>
          <ArrowUpRight size={18} className="text-summit-forest shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </motion.div>
    </div>
  </section>
);

const Challenge = () => (
  <section id="challenge" data-testid="challenge-section" className="py-24 md:py-32 bg-summit-sand">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-7">
          <SectionHeading eyebrow="Why AI and Transformation Initiatives Struggle" title="AI initiatives rarely fail for lack of technology." />
        </div>
        <motion.div {...stagger(2)} className="lg:col-span-5 lg:pt-14">
          <p className="font-heading text-2xl md:text-3xl text-summit-charcoal leading-snug">
            They struggle when strategy, leadership, governance, people, and adoption are not aligned.
          </p>
          <p className="mt-4 font-body text-base text-summit-text-muted leading-relaxed">
            The opportunity is real. Realizing it requires organizational readiness and disciplined transformation.
          </p>
        </motion.div>
      </div>
      <DimensionsGraphic />
    </div>
  </section>
);

const AILead = () => {
  const ai = SERVICES[0];
  return (
    <section id="ai-adoption" data-testid="ai-lead-section" className="py-24 md:py-32 bg-summit-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="border-t-2 border-summit-gold pt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Lead Offering" title={ai.title} />
            <motion.p {...stagger(2)} className="mt-6 font-heading text-2xl md:text-3xl text-summit-charcoal leading-snug">
              Moving AI from experimentation to enterprise impact.
            </motion.p>
            <motion.p {...stagger(3)} className="mt-5 font-body text-base text-summit-text-muted leading-relaxed">
              {ai.heroCopy}
            </motion.p>
            <motion.div {...stagger(4)} className="mt-8">
              <PrimaryLink to={`/services/${ai.slug}`} testId="ai-lead-cta">Talk With Summit About AI</PrimaryLink>
            </motion.div>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-l border-summit-border">
              {ai.offerings.map((o, i) => (
                <motion.div key={o.title} {...stagger(i * 0.6)} data-testid={`ai-offering-${i}`} className="border-b border-r border-summit-border hover:bg-summit-sand transition-colors p-6 flex items-start gap-4">
                  <span className="font-heading text-xl text-summit-gold-dark leading-none mt-1 w-7 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span>
                    <span className="block font-heading text-xl text-summit-charcoal leading-tight">{o.title}</span>
                    <span className="block mt-1.5 font-body text-sm text-summit-text-muted leading-relaxed">{o.description}</span>
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Framework = () => (
  <section id="framework" data-testid="framework-section" className="py-24 md:py-32 bg-white border-y border-summit-border">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-7">
          <SectionHeading eyebrow="Proprietary Methodology" title={FRAMEWORK.name} copy={FRAMEWORK.tagline} />
        </div>
        <motion.div {...stagger(2)} className="lg:col-span-5 lg:pt-14">
          <p className="font-heading text-2xl md:text-3xl text-summit-charcoal leading-snug">
            The methodology behind every Summit engagement—from strategy to sustainable transformation.
          </p>
          <ul className="mt-6 space-y-2 font-body text-sm text-summit-text-muted">
            {["Five phases, one outcome", "Applied across all four service areas", "Built from leading enterprise transformation, not theory"].map((t) => (
              <li key={t} className="flex items-center gap-3"><span className="w-1.5 h-1.5 rotate-45 bg-summit-gold shrink-0" />{t}</li>
            ))}
          </ul>
        </motion.div>
      </div>
      <FrameworkSection />
      <motion.div {...stagger(1)} className="mt-14 flex flex-col sm:flex-row sm:items-center gap-5">
        <PrimaryLink to="/services/organizational-transformation" testId="framework-cta">Discuss Your Transformation</PrimaryLink>
        <p className="font-body text-xs text-summit-text-muted">Summit Transformation Framework™ is proprietary to Summit Transformation Group.</p>
      </motion.div>
    </div>
  </section>
);

const Services = () => (
  <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-summit-bg">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHeading eyebrow="Four Core Service Areas" title="One firm. Four connected capabilities." className="mb-16" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-summit-border">
        {SERVICES.map((s, i) => (
          <motion.div key={s.slug} {...stagger(i)} className={`${s.lead ? "bg-summit-forest text-white" : "bg-summit-bg"} h-full border-b border-r border-summit-border`}>
            <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group flex flex-col h-full p-8 lg:p-9">
              <span className={`font-body text-xs uppercase tracking-[0.2em] ${s.lead ? "text-summit-gold-light" : "text-summit-gold-dark"}`}>
                {s.badge || `0${i + 1}`}
              </span>
              <h3 className={`mt-6 font-heading text-2xl lg:text-3xl font-medium leading-tight ${s.lead ? "text-white" : "text-summit-charcoal"}`}>{s.title}</h3>
              <p className={`mt-4 font-body text-sm leading-relaxed flex-1 ${s.lead ? "text-white/70" : "text-summit-text-muted"}`}>{s.summary}</p>
              <span className={`mt-8 inline-flex items-center gap-2 font-body text-sm font-semibold ${s.lead ? "text-summit-gold-light" : "text-summit-forest group-hover:text-summit-gold-dark"} transition-colors`}>
                Explore {s.shortTitle} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const WHY = [
  { title: "Executive experience, not theory", copy: "Grounded in leading enterprise-wide transformation inside complex institutions." },
  { title: "From strategy through adoption", copy: "Engaged from strategy formation to leadership alignment, readiness, and sustained adoption." },
  { title: "A proprietary system", copy: "The Summit Transformation Framework™ gives leadership teams a shared language and a disciplined path." },
  { title: "Independent of technology", copy: "Not a vendor. Our advice is never tied to selling tools, platforms, or implementations." },
];

const Why = () => (
  <section id="why-summit" data-testid="why-summit-section" className="relative py-24 md:py-32 bg-summit-forest text-white overflow-hidden grain">
    <div className="relative max-w-7xl mx-auto px-6 md:px-10">
      <SectionHeading light eyebrow="Why Summit" title="An executive-level transformation advisory firm." copy="Not a technology vendor. Not a training company. Not a generic change management consultancy." className="mb-16" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-10">
        {WHY.map((w, i) => (
          <motion.div key={w.title} {...stagger(i)} data-testid={`why-item-${i}`} className="border-t border-white/15 pt-6">
            <span className="font-body text-xs tracking-[0.2em] text-summit-gold-light">0{i + 1}</span>
            <h3 className="mt-3 font-heading text-2xl font-medium leading-tight">{w.title}</h3>
            <p className="mt-3 font-body text-sm text-white/70 leading-relaxed">{w.copy}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const SECTORS = ["Higher education", "Nonprofit organizations", "Museums and cultural institutions", "Mid-sized businesses", "Complex organizations in transformation", "Executive teams scaling AI adoption"];

const Expertise = () => (
  <section id="expertise" data-testid="expertise-section" className="py-24 md:py-32 bg-summit-sand overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-7">
        <SectionHeading
          eyebrow="Representative Expertise"
          title="Experience navigating complex institutions."
          copy="More than two decades leading and advising on change across Fortune 500 companies, nonprofits, government agencies, and higher education—including enterprise-wide transformation at one of the nation's largest universities."
        />
        <motion.ul {...stagger(2)} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {SECTORS.map((s) => (
            <li key={s} className="flex items-start gap-3 font-body text-sm md:text-base text-summit-charcoal">
              <span className="mt-2 w-1.5 h-1.5 rotate-45 bg-summit-gold shrink-0" /> {s}
            </li>
          ))}
        </motion.ul>
        <motion.div {...stagger(3)} className="mt-10">
          <TextLink to="/about" testId="expertise-about-link">About the firm and its founder</TextLink>
        </motion.div>
      </div>
      <motion.div {...stagger(1)} className="lg:col-span-5">
        <div className="relative max-w-sm mx-auto lg:ml-auto">
          <div className="hidden sm:block absolute -inset-3 border border-summit-gold/50 translate-x-4 translate-y-4" />
          <img src={HEADSHOT_URL} alt="Jeremy Bean, Founder and Principal, Summit Transformation Group" className="relative w-full aspect-[4/5] object-cover object-top" />
          <div className="relative bg-summit-forest text-white px-6 py-5">
            <p className="font-heading text-xl">Jeremy Bean</p>
            <p className="font-body text-xs uppercase tracking-[0.18em] text-summit-gold-light mt-1">Founder & Principal</p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const Insights = () => (
  <section id="insights" data-testid="insights-section" className="py-24 md:py-32 bg-summit-bg">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <SectionHeading eyebrow="Insights" title="Perspectives for leaders navigating AI and transformation." />
        <TextLink to="/insights" testId="insights-all-link" className="shrink-0">All insights</TextLink>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {INSIGHTS.slice(0, 3).map((p, i) => (
          <motion.article key={p.slug} {...stagger(i)} data-testid={`insight-card-${i}`} className="card-lift group border border-summit-border bg-white p-8 flex flex-col">
            <span className="font-body text-xs uppercase tracking-[0.18em] text-summit-gold-dark">{p.category}</span>
            <h3 className="mt-4 font-heading text-2xl font-medium leading-snug text-summit-charcoal">{p.title}</h3>
            <p className="mt-4 font-body text-sm text-summit-text-muted leading-relaxed flex-1">{p.excerpt}</p>
            <span className="mt-6 font-body text-xs text-summit-text-muted">{p.readTime} · Coming soon</span>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  useSEO(
    "AI Strategy & Organizational Transformation Consulting",
    "Summit Transformation Group is an executive-level advisory firm helping organizations adopt AI, lead complex transformation, align leadership, and build lasting organizational capability."
  );
  return (
    <>
      <Hero />
      <Challenge />
      <AILead />
      <Framework />
      <Services />
      <Why />
      <Expertise />
      <Insights />
      <CTASection
        eyebrow="An Invitation"
        heading="Transformation does not begin with technology."
        copy="It begins with clarity about where you are going and the organizational capability to get there."
        testId="home-cta"
      />
    </>
  );
}
